import React, { useEffect, useState, useRef, forwardRef, useImperativeHandle } from "react";
import {
    Box,
    Typography,
    Grid,
    Chip,
    Avatar,
    Button,
    Skeleton,
    Alert,
    Stack,
    TextField,
    InputAdornment
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { draggable } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import axios from "axios";
import { uriConfig } from '../../data/uri';
import { useAuth0 } from '@auth0/auth0-react';
import Colours from "../ColourFunction/ColourFunction";
import StyledDivider from "../StyledDivider/StyledDivider";
import SearchableUserList from "../SearchableUserList/SearchableUserList";

const Auth0Groups = forwardRef((props, ref) => {
    const { authToken, userList = [], onError, ...others } = props;
    const { user } = useAuth0();

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [pendingChanges, setPendingChanges] = useState([]);
    const [saving, setSaving] = useState(false);
    const [localGroupState, setLocalGroupState] = useState([]);

    // Global search state for filtering all boxes
    const [globalSearch, setGlobalSearch] = useState('');

    // Refs for drag and drop
    const dragRefs = useRef({});
    const dropRefs = useRef({});

    // Fetch group memberships
    const fetchGroupMemberships = async () => {
        if (!authToken || !user?.sub) return;

        setLoading(true);
        try {
            const url = uriConfig.url.getAuth0GroupMembers;

            const response = await axios.get(url, {
                headers: { 'auth-authorization': 'Bearer ' + authToken }
            });

            setGroups(response.data);
            setLocalGroupState(response.data);
            setError(null);
        } catch (err) {
            console.error('Error fetching group memberships:', err);
            setError('Failed to load group memberships');
            if (onError) onError(err);
        } finally {
            setLoading(false);
        }
    };

    // Expose fetchGroupMemberships to parent components via ref
    useImperativeHandle(ref, () => ({
        fetchGroupMemberships
    }));
    // Add group name mapping - rename the Auth0 name to a more user friendly name
    const groupNameMapping = {
        'QlikAdmin': 'Qlik Contributor',
        'QlikDefault': 'Qlik Viewer',
        // Add more mappings as needed
    };

    // Helper function to get display name
    const getDisplayName = (groupName) => {
        return groupNameMapping[groupName] || groupName;
    };

    // Helper function to filter users by search term
    const filterUsers = (users, searchTerm) => {
        if (!searchTerm || searchTerm.trim() === '') {
            return users;
        }

        const lowerSearch = searchTerm.toLowerCase();
        return users.filter(user => {
            const fullName = (user.FullName || user.fullname || '').toLowerCase();
            const email = (user.Email || user.email || '').toLowerCase();
            return fullName.includes(lowerSearch) || email.includes(lowerSearch);
        });
    };

    // Get users not in any group (including pending changes)
    const getUnassignedUsers = () => {
        if (!userList.length) return [];

        // Use the same logic as getDisplayGroups to calculate final group membership
        const displayGroups = getDisplayGroups();
        const assignedUserSubs = new Set();

        displayGroups.forEach(group => {
            group.members.forEach(member => {
                assignedUserSubs.add(member.sub);
            });
        });

        return userList.filter(user => !assignedUserSubs.has(user.user_sub) && !user.IsDeleted);
    };

    // Apply pending changes to local state for display
    const getDisplayGroups = () => {
        const displayGroups = JSON.parse(JSON.stringify(localGroupState)); // Deep clone

        // Group pending changes by user for better processing
        const changesByUser = {};
        pendingChanges.forEach(change => {
            if (!changesByUser[change.user_sub]) {
                changesByUser[change.user_sub] = [];
            }
            changesByUser[change.user_sub].push(change);
        });

        // Process changes for each user independently
        Object.entries(changesByUser).forEach(([user_sub, userChanges]) => {
            const userData = userList.find(u => u.user_sub === user_sub) ||
                localGroupState.flatMap(g => g.members).find(m => m.sub === user_sub);

            if (!userData) return;

            // Convert userList format to member format if needed
            const memberData = userData.FullName ? {
                fullname: userData.FullName,
                email: userData.Email,
                sub: userData.user_sub
            } : userData;

            // Track which groups this user should be in after all changes
            const finalGroups = new Set();

            // Start with current group memberships
            displayGroups.forEach(group => {
                if (group.members.some(m => m.sub === user_sub)) {
                    finalGroups.add(group.id);
                }
            });

            // Apply all changes for this user
            userChanges.forEach(change => {
                const { from_group_id, to_group_id } = change;

                // Remove from source group
                if (from_group_id) {
                    finalGroups.delete(from_group_id);
                }

                // Add to target group (null means remove from all)
                if (to_group_id !== null) {
                    finalGroups.add(to_group_id);
                }
            });

            // Update all groups based on final membership
            displayGroups.forEach(group => {
                const shouldBeInGroup = finalGroups.has(group.id);
                const currentlyInGroup = group.members.some(m => m.sub === user_sub);

                if (shouldBeInGroup && !currentlyInGroup) {
                    // Add user to group
                    group.members.push(memberData);
                } else if (!shouldBeInGroup && currentlyInGroup) {
                    // Remove user from group
                    group.members = group.members.filter(m => m.sub !== user_sub);
                }
            });
        });

        return displayGroups;
    };

    // Save changes to API
    const saveChanges = async () => {
        if (pendingChanges.length === 0) return;

        setSaving(true);
        try {
            const url = uriConfig.url.postEditAuth0GroupMembers;

            const response = await axios.post(url, pendingChanges, {
                headers: { 'auth-authorization': 'Bearer ' + authToken }
            });

            // Refresh data after successful save
            await fetchGroupMemberships();
            setPendingChanges([]);
            setError(null);
        } catch (err) {
            console.error('Error saving group changes:', err);
            setError('Failed to save changes');
            if (onError) onError(err);
        } finally {
            setSaving(false);
        }
    };

    // Handle drop events
    const handleDrop = (targetGroupId, droppedUserSub, sourceGroupId = null) => {
        // Don't add duplicate changes
        const existingChangeIndex = pendingChanges.findIndex(
            change => change.user_sub === droppedUserSub && change.to_group_id === targetGroupId
        );

        if (existingChangeIndex !== -1) return;

        const newChange = {
            user_sub: droppedUserSub,
            from_group_id: sourceGroupId,
            to_group_id: targetGroupId
        };

        setPendingChanges(prev => [...prev, newChange]);
    };

    // Setup drop targets only
    useEffect(() => {
        const cleanupFunctions = [];

        // Setup drop targets for groups
        groups.forEach(group => {
            const element = dropRefs.current[group.id];
            if (element) {
                const cleanup = dropTargetForElements({
                    element,
                    getData: () => ({ groupId: group.id }),
                    onDrop: ({ source }) => {
                        if (source.data.type === 'user') {
                            handleDrop(group.id, source.data.userSub, source.data.sourceGroupId);
                        }
                    }
                });
                cleanupFunctions.push(cleanup);
            }
        });

        // Setup drop target for "No Groups"
        const noGroupElement = dropRefs.current['no-groups'];
        if (noGroupElement) {
            const cleanup = dropTargetForElements({
                element: noGroupElement,
                getData: () => ({ groupId: null }),
                onDrop: ({ source }) => {
                    if (source.data.type === 'user' && source.data.source === 'group') {
                        handleDrop(null, source.data.userSub, source.data.sourceGroupId);
                    }
                }
            });
            cleanupFunctions.push(cleanup);
        }

        // Setup global drop zone for removal
        const globalDropElement = dropRefs.current['global-drop'];
        if (globalDropElement) {
            const cleanup = dropTargetForElements({
                element: globalDropElement,
                getData: () => ({ globalDrop: true }),
                onDrop: ({ source }) => {
                    if (source.data.type === 'user' && source.data.source === 'group') {
                        handleDrop(null, source.data.userSub, source.data.sourceGroupId);
                    }
                }
            });
            cleanupFunctions.push(cleanup);
        }

        return () => {
            cleanupFunctions.forEach(cleanup => cleanup());
        };
    }, [groups]); // Only re-run when groups change

    useEffect(() => {
        fetchGroupMemberships();
    }, [authToken, user]);

    useEffect(() => {
        if (!loading) {
            // Reset pending, refresh computed group display and allow new users to appear
            setPendingChanges([]);
            setLocalGroupState(groups);
        }
    }, [userList, groups, loading]);

    // User Card Component - each card registers its own drag handlers
    const UserCard = ({ user, isDraggable = true, source = 'available', groupId = null }) => {
        const userSub = user.user_sub || user.sub;
        const userName = user.FullName || user.fullname || '';
        const ref = useRef(null);

        useEffect(() => {
            const element = ref.current;
            if (!element || !isDraggable) return;

            const cleanup = draggable({
                element,
                getInitialData: () => ({
                    type: 'user',
                    userSub: userSub,
                    userData: user,
                    source: source,
                    sourceGroupId: groupId
                })
            });

            return cleanup;
        }, [userSub, isDraggable, source, groupId, user]);

        return (
            <Chip
                ref={ref}
                avatar={
                    <Avatar
                        {...Colours.ColourFunction(userName)}
                        sx={{
                            width: 24,
                            height: 24,
                            ...Colours.ColourFunction(userName).sx
                        }}
                    />
                }
                label={`${userName} (${user.Email || user.email})`}
                variant="outlined"
                size="small"
                sx={{
                    margin: 0.5,
                    cursor: isDraggable ? 'grab' : 'default',
                    '&:active': {
                        cursor: isDraggable ? 'grabbing' : 'default'
                    }
                }}
                draggable={isDraggable}
            />
        );
    };

    if (loading) {
        return (
            <Box sx={{ width: '100%', mt: 2 }}>
                <Skeleton variant="rectangular" height={200} />
            </Box>
        );
    }

    if (error) {
        return (
            <Alert severity="error" sx={{ mt: 2 }}>
                {error}
                <Button onClick={fetchGroupMemberships} sx={{ ml: 2 }}>
                    Retry
                </Button>
            </Alert>
        );
    }


    const unassignedUsers = getUnassignedUsers();
    const displayGroups = getDisplayGroups();

    return (
        <Box
            ref={el => dropRefs.current['global-drop'] = el}
            sx={{
                width: '100%',
                mt: 2,
                minHeight: '400px',
                position: 'relative'
            }}
        >
            {/* Global Search Bar */}
            <Box sx={{ mb: 3 }}>
                <TextField
                    fullWidth
                    placeholder="Search all users..."
                    value={globalSearch}
                    onChange={(e) => setGlobalSearch(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon />
                            </InputAdornment>
                        ),
                    }}
                    sx={{ maxWidth: 600 }}
                />
                {globalSearch && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                        Filtering all boxes by: "{globalSearch}"
                    </Typography>
                )}
            </Box>

            {pendingChanges.length > 0 && (
                <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
                    <Typography variant="body2" color="text.secondary">
                        Pending changes
                    </Typography>
                    <Button
                        variant="contained"
                        onClick={saveChanges}
                        disabled={saving}
                        size="small"
                    >
                        {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                    <Button
                        onClick={() => {
                            setPendingChanges([]);
                            setLocalGroupState(groups); // Reset to original state
                        }}
                        disabled={saving}
                        size="small"
                    >
                        Cancel
                    </Button>
                </Box>
            )}

            <Grid container spacing={3}>
                {/* Left Column: Available Users & No Groups */}
                <Grid item xs={12} md={4}>
                    <Stack spacing={2}>
                        {/* Available Users */}
                        <SearchableUserList
                            users={filterUsers(userList.filter(u => !u.IsDeleted), globalSearch)}
                            title="Available Users"
                            subtitle="Drag users to assign them to groups"
                            renderUser={(user) => <UserCard user={user} source="available" />}
                            minHeight={120}
                            maxHeight={350}
                            showSearch={false}
                        />

                        {/* No Groups - only show if there are unassigned users */}
                        {unassignedUsers.length > 0 && (
                            <SearchableUserList
                                users={filterUsers(unassignedUsers, globalSearch)}
                                title="Unassigned Users"
                                subtitle={`Users not assigned to any group (${unassignedUsers.length})`}
                                renderUser={(user) => <UserCard user={user} isDraggable={true} source="unassigned" />}
                                dropRef={el => dropRefs.current['no-groups'] = el}
                                minHeight={80}
                                maxHeight={300}
                                showSearch={false}
                                cardSx={{
                                    border: '2px dashed #ccc',
                                    '&:hover': { borderColor: '#999' },
                                    borderColor: 'primary.main'
                                }}
                            />
                        )}
                    </Stack>
                </Grid>

                {/* Right Column: Groups - takes up more space */}
                <Grid item xs={12} md={8}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {displayGroups.map(group => {
                            const filteredMembers = filterUsers(group.members, globalSearch);

                            return (
                                <SearchableUserList
                                    key={group.id}
                                    users={filteredMembers}
                                    renderUser={(member) => (
                                        <UserCard
                                            user={member}
                                            isDraggable={true}
                                            source="group"
                                            groupId={group.id}
                                        />
                                    )}
                                    dropRef={el => dropRefs.current[group.id] = el}
                                    minHeight={80}
                                    maxHeight={300}
                                    showSearch={false}
                                    cardSx={{
                                        border: '2px  #ccc',
                                        '&:hover': { borderColor: '#999' },
                                        borderColor: 'primary.main'
                                    }}
                                    emptyMessage="No members found"
                                    customHeader={
                                        <Box sx={{ backgroundColor: group.name === 'Super User' ? 'rgb(195, 47, 38)' : 'primary.main', fontWeight: 'bold', color: 'white', p: 2, m: -2, mt: -2, mb: 1, borderRadius: '4px 4px 0 0' }}>
                                            <Typography variant="h6" gutterBottom>
                                                {getDisplayName(group.name)}
                                            </Typography>
                                            {group.description && (
                                                <Typography variant="body2" color="white" gutterBottom>
                                                    {group.description}
                                                </Typography>
                                            )}
                                            <Typography variant="body2" color="white" gutterBottom>
                                                {group.members.length} members
                                            </Typography>
                                        </Box>
                                    }
                                />
                            );
                        })}
                    </Box>
                </Grid>
            </Grid>
        </Box>
    );
});

export default Auth0Groups;